// SPDX-License-Identifier: AGPL-3.0-or-later

// The 3D room stage a bot runs: the API writes it to the LiveKit room metadata as {"stage": …} and sends its one-off
// moments as server data packets on VOICE_ROOM_STAGE_EFFECT_TOPIC; clients only read both.
export const VOICE_ROOM_STAGE_EFFECT_TOPIC = 'micup.stage.fx';

// The host character's identity in the room's character layer; never a voice identity (user_<id>_<connection>).
export const VOICE_ROOM_STAGE_HOST_IDENTITY_PREFIX = 'stage_';

export const VOICE_ROOM_STAGE_BOARD_STYLES = ['neon', 'tahta', 'sade'] as const;

export const VOICE_ROOM_STAGE_HOST_EMOTES = ['wave', 'dance', 'jump', 'emoji'] as const;

export const VOICE_ROOM_STAGE_LIMITS = {
	titleMax: 60,
	textMax: 280,
	textLinesMax: 4,
	scoresMax: 10,
	scoreNameMax: 32,
	scoreValueMax: 1_000_000_000,
	buttonsMax: 5,
	buttonLabelMax: 80,
	customIdMax: 100,
	botNameMax: 32,
	emojiMax: 16,
	// The encoded room metadata; LiveKit's own limit is far above it.
	metadataBytesMax: 4096,
	effectBytesMax: 256,
	ttlSecondsMin: 60,
	ttlSecondsMax: 21_600,
	ttlSecondsDefault: 3600,
} as const;

// Plain text only: no control character (a text block keeps its line breaks), no line or paragraph separator, no
// direction mark or override and no invisible character that could make a name read as another. The zero-width
// joiner and non-joiner stay: emoji sequences and several scripts need them.
function isHidden(code: number): boolean {
	return (
		(code >= 0x7f && code <= 0x9f) ||
		code === 0x061c ||
		code === 0x200b ||
		code === 0x200e ||
		code === 0x200f ||
		(code >= 0x2028 && code <= 0x202e) ||
		code === 0x2060 ||
		(code >= 0x2066 && code <= 0x2069) ||
		code === 0xfeff
	);
}

function isPlain(text: string, lineBreaks: boolean): boolean {
	for (let i = 0; i < text.length; i++) {
		const code = text.charCodeAt(i);
		if ((code < 0x20 && !(lineBreaks && code === 0x0a)) || isHidden(code)) return false;
	}
	return true;
}

export function isVoiceRoomStageLine(text: string): boolean {
	return isPlain(text, false);
}

export function isVoiceRoomStageBlock(text: string): boolean {
	return isPlain(text, true) && text.split('\n').length <= VOICE_ROOM_STAGE_LIMITS.textLinesMax;
}
